'use client';

import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@pickleballinc/ui/components/ui/dropdown-menu';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';

import { TYPE_CONFIG } from '@/modules/players/v2/tabs/constants';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { LeaguesStatus } from '@/types/leagues.types';
import {
  ModuleType,
  PlayerParticipationType,
  playerParticipationTypeMap,
} from '@/types/players.types';

interface SubNavigationTabsProps {
  isUserAlsoPlayer: boolean | undefined;

  type: ModuleType;
}

export function SubNavigationTabs({ isUserAlsoPlayer, type }: SubNavigationTabsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { hasPermission } = useAuth();

  const isPickleballSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isPBSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);
  const isSuperAdmin = isPBSuperAdmin || isPickleballSuperAdmin;

  const config = TYPE_CONFIG[type];
  const searchParamsType = searchParams.get(config.typeParamKey) || '';
  const statusValue = config.statusKey ? searchParams.get(config.statusKey) : null;

  const subNavItems = config.typeItems.filter((item) => {
    if (item.name === 'Managing') {
      return isSuperAdmin || isUserAlsoPlayer;
    }
    if (item.name === 'Favorites') {
      return isSuperAdmin || isUserAlsoPlayer;
    }
    return true;
  });

  const activeTypeItem = config.typeItems.find((t) => t.param === searchParamsType);
  const typeButton = activeTypeItem?.name || PlayerParticipationType.RegisteredFor;

  const handleTypeChange = (newType: string) => {
    const newSearchParams = new URLSearchParams(searchParams?.toString());
    newSearchParams.set(config.typeParamKey, newType);
    newSearchParams.delete('search');
    if (type === 'league') {
      const leagueType = playerParticipationTypeMap[newType];

      const newStatus =
        leagueType === PlayerParticipationType.Favorited
          ? LeaguesStatus.All
          : statusValue && statusValue !== LeaguesStatus.All
            ? statusValue
            : LeaguesStatus.Current;

      newSearchParams.set('status', newStatus);
    }

    if (type === 'club') {
      newSearchParams.set('page', '1');
    }

    router.replace(`?${newSearchParams?.toString()}`);
  };

  return (
    <div className='flex flex-col gap-6 sm:gap-10 w-full items-center'>
      <div className='w-full sm:hidden flex'>
        {subNavItems.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger className='outline-none w-full'>
              <div className='relative flex items-center justify-between px-[14px] py-[10px] text-base text-gray-600 bg-white font-medium rounded-md shadow-xs'>
                {typeButton}
                <FontAwesomeIcon icon={faChevronDown} size='sm' />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='flex flex-col text-base text-gray-600 bg-white font-medium w-[--radix-dropdown-menu-trigger-width]'>
              {subNavItems
                .filter(({ name }) => name !== typeButton)
                .map((item) => (
                  <DropdownMenuItem
                    key={`mobile-navigation-item-${item.name}`}
                    className='flex text-base cursor-pointer !w-full'
                    onClick={() => handleTypeChange(item.param)}
                  >
                    {item.name}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className='w-full flex items-center px-[14px] py-[10px] text-base text-gray-600 bg-white font-medium rounded-md shadow-xs'>
            {typeButton}
          </div>
        )}
      </div>

      <div className='hidden sm:flex w-full rounded-lg bg-purple-50 border border-purple-100 mx-auto px-0 justify-center'>
        <ul className='flex w-full sm:max-w-screen-xl items-center p-1 sm:p-1.5'>
          {subNavItems.map((item) => {
            return (
              <li key={`desktop-navigation-item-${item.name}`} className='flex-1'>
                <Button
                  variant={typeButton === item.name ? 'primary' : 'secondary'}
                  size='md'
                  className={clsx(
                    'flex w-full md:max-w-[361px] lg:max-w-[491px] justify-center text-sm sm:text-base font-semibold rounded-md px-2.5 sm:px-3.5 p-2.5 !shadow-[unset]',
                    {
                      'bg-purple-600 text-white': typeButton === item.name,
                      '!text-gray-700 !bg-transparent hover:!text-opacity-60':
                        typeButton !== item.name,
                    }
                  )}
                  onClick={() => handleTypeChange(item.param)}
                  data-testid={`players-${type}-${item.param}`}
                >
                  {item.name}
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
